class ProductSpotlight extends HTMLElement {
	static TRANSITION_DURATION = 450;
	static ACTIVE_CLASS = 'is-active';

	constructor() {
		super();

		this.tabs = Array.from(this.querySelectorAll('.product-spotlight__tab'));
		this.images = this.querySelectorAll('.product-spotlight__image-wrap');
		this.contents = this.querySelectorAll('.product-spotlight__content');

		if (this.tabs.length === 0) {
			return;
		}

		this.attachEventListeners();
	}

	attachEventListeners() {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => this.switchTab(tab));
		});

		const tablist = this.querySelector('[role="tablist"]');
		if (tablist) {
			tablist.addEventListener('keydown', (event) => this.handleKeydown(event));
		}
	}

	handleKeydown(event) {
		const currentIndex = this.tabs.indexOf(event.target);
		if (currentIndex === -1) {
			return;
		}

		const targetIndex = this.resolveKeyboardTarget(event.key, currentIndex);
		if (targetIndex === null) {
			return;
		}

		event.preventDefault();
		this.switchTab(this.tabs[targetIndex]);
		this.tabs[targetIndex].focus();
	}

	resolveKeyboardTarget(key, currentIndex) {
		const lastIndex = this.tabs.length - 1;

		const keyMap = {
			ArrowRight: Math.min(currentIndex + 1, lastIndex),
			ArrowLeft: Math.max(currentIndex - 1, 0),
			Home: 0,
			End: lastIndex,
		};

		return keyMap[key] ?? null;
	}

	switchTab(tab) {
		const index = tab.getAttribute('data-index');
		const oldDescriptionHeight = this.measureActiveDescriptionHeight();

		this.deactivateAll();
		this.activateTab(tab, index);
		this.animateDescription(index, oldDescriptionHeight);
	}

	measureActiveDescriptionHeight() {
		const activeContent = this.querySelector(`.product-spotlight__content.${ProductSpotlight.ACTIVE_CLASS}`);
		if (!activeContent) {
			return 0;
		}

		const description = activeContent.querySelector('.product-spotlight__description');
		return description ? description.offsetHeight : 0;
	}

	deactivateAll() {
		this.tabs.forEach((t) => {
			t.classList.remove(ProductSpotlight.ACTIVE_CLASS);
			t.setAttribute('aria-selected', 'false');
			t.setAttribute('tabindex', '-1');
		});

		this.images.forEach((img) => img.classList.remove(ProductSpotlight.ACTIVE_CLASS));
		this.contents.forEach((c) => {
			c.classList.remove(ProductSpotlight.ACTIVE_CLASS);
			c.setAttribute('hidden', '');
		});
	}

	activateTab(tab, index) {
		tab.classList.add(ProductSpotlight.ACTIVE_CLASS);
		tab.setAttribute('aria-selected', 'true');
		tab.setAttribute('tabindex', '0');

		const image = this.querySelector(`.product-spotlight__image-wrap[data-index="${index}"]`);
		if (image) {
			image.classList.add(ProductSpotlight.ACTIVE_CLASS);
		}

		const content = this.querySelector(`.product-spotlight__content[data-index="${index}"]`);
		if (content) {
			content.classList.add(ProductSpotlight.ACTIVE_CLASS);
			content.removeAttribute('hidden');
		}
	}

	animateDescription(index, oldHeight) {
		const content = this.querySelector(`.product-spotlight__content[data-index="${index}"]`);
		if (!content) {
			return;
		}

		const description = content.querySelector('.product-spotlight__description');
		if (!description) {
			return;
		}

		const newHeight = description.scrollHeight;
		description.style.overflow = 'hidden';
		description.style.height = oldHeight + 'px';

		requestAnimationFrame(() => {
			description.style.height = newHeight + 'px';
			setTimeout(() => {
				description.style.overflow = '';
				description.style.height = '';
			}, ProductSpotlight.TRANSITION_DURATION);
		});
	}
}

if (!customElements.get('product-spotlight')) {
	customElements.define('product-spotlight', ProductSpotlight);
}
